import { Box, InternalGrid } from '@paygreen/paygreen-ui';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { withTranslation } from 'react-i18next';
import {
    ErrorBoundary,
    H1Title,
    NotAvailableForTestMessage,
    ToggleMode,
} from '../../../components';
import { useAPIError, useMode } from '../../../context';
import { MODES } from '../../../utils/constants';
import { IsBigDesktop } from '../../../utils/tools';
import { getProjects } from '../../projects/service';
import { getAllContributions } from '../service';
import ContributionsTable from './ContributionsTable';

const AllMyContributions = ({ t }) => {
    const { addError } = useAPIError();
    const { mode } = useMode();

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);

    const [isLoading, setIsLoading] = useState(true);
    const [dataContributions, setDataContributions] = useState([]);
    const [pageCount, setPageCount] = useState(0);
    const [totalItems, setTotalItems] = useState(0);

    const fetchData = useCallback(
        (pageSize = 25, pageIndex = 1, statusFilter = null) => {
            isMounted.current && setIsLoading(true);

            const params = {
                limit: pageSize,
                page: pageIndex,
                status: statusFilter || null,
            };

            getProjects()
                .then((resProjects) => {
                    const projects = resProjects?.data?._embedded?.project;
                    getAllContributions(params)
                        .then((res) => {
                            if (mode === MODES.TEST) {
                                // we simulate test mode for contribution with no data
                                isMounted.current && setDataContributions([]);
                            } else if (isMounted.current) {
                                setDataContributions(
                                    res.data._embedded.carbon_contribution.map(
                                        (contrib) => ({
                                            ...contrib,
                                            project: projects.find(
                                                (proj) =>
                                                    proj?.idProject ===
                                                    contrib?.id_carbon_project,
                                            ),
                                        }),
                                    ),
                                );
                                setPageCount(res.data.page_count);
                                setTotalItems(res.data.total_items);
                            }
                        })
                        .finally(() => {
                            isMounted.current && setIsLoading(false);
                        });
                })
                .catch((error) => {
                    if (error?.response?.data.status === 404) {
                        isMounted.current && setDataContributions([]);
                    } else {
                        addError(
                            error?.response?.data.detail,
                            error?.response?.data.status,
                        );
                    }
                });
        },
        [addError, mode],
    );

    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;
        };
    }, []);

    return (
        <>
            <Helmet>
                <title>
                    {t('contributions.allMyContributions.metaTitle')} -{' '}
                    {t('main.metaTitle')}
                </title>
            </Helmet>

            <InternalGrid
                displayType="grid"
                alignItems="flex-start"
                justifyItems="start"
                hasStaticWidth={false}
                gridTemplateColumns="auto"
                gridGap="xs"
            >
                <H1Title title={t('contributions.allMyContributions.title')}>
                    <ToggleMode />
                </H1Title>

                {mode === MODES.TEST && <NotAvailableForTestMessage />}
            </InternalGrid>

            <Box marginTop="lg">
                <InternalGrid
                    hasStaticWidth={true}
                    displayType={IsBigDesktop() ? 'flex' : 'grid'}
                    gridGap="md"
                    gridTemplateColumns="1fr"
                    justifyItems="stretch"
                >
                    <ErrorBoundary>
                        <ContributionsTable
                            data={dataContributions}
                            fetchData={fetchData}
                            loading={isLoading}
                            pageCount={pageCount}
                            totalItems={totalItems}
                        />
                    </ErrorBoundary>
                </InternalGrid>
            </Box>
        </>
    );
};

export default withTranslation('common')(AllMyContributions);
